/**
 * Copyright 2025, IntraLinks, Inc. All rights reserved.
 */

import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../store';
import { I18nNamespaces, LOA_THREE, LOA_TWO } from '../utils/constants.util';
import { useRequiredLevel } from '../utils/helpers.util';
import usePreserveSearchNav from './usePreserveSearchNav';
import { navigateDefaultProviderForLevel } from '../../../features/SecuritySetup/LoaIntegrations/loaIntegrationsSlice';
import { requireUserId } from '../../../features/Profile/profileSlice';
import { getFlowSnapshot } from '../utils/popup.util';
import { SecuritySetupRoutes } from '../utils/securitySetupRoutes';

export default function useSecuritySetupFlow() {
  const profileDetails = useSelector((s: RootState) => s.profile.profileDetails);
  const requiredLevel = useRequiredLevel();
  const { replacePreserve } = usePreserveSearchNav();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const { t } = useTranslation(I18nNamespaces.DEFAULT);

  const hasRealProfile = !!profileDetails?.id;
  const ssoEnabled = hasRealProfile ? profileDetails.ssoEnabled : undefined;
  const requestedProfileRef = useRef(false);

  useEffect(() => {
    if (hasRealProfile) {
      requestedProfileRef.current = false;
      return;
    }

    if (!requestedProfileRef.current) {
      requestedProfileRef.current = true;
      dispatch(requireUserId());
    }
  }, [hasRealProfile, dispatch]);

  useEffect(() => {
    if (!hasRealProfile) {
      return;
    }

    if (ssoEnabled === undefined) {
      return;
    };

    const isEntryRoute = pathname === SecuritySetupRoutes.root || pathname === `${SecuritySetupRoutes.root}/`;

    if (!isEntryRoute) return;

    const { mode, applicationId } = getFlowSnapshot();

    if (mode === 'redirect' && !applicationId) {
      replacePreserve(SecuritySetupRoutes.error);
      return;
    }

    if (ssoEnabled) {
      replacePreserve(SecuritySetupRoutes.root);
      return;
    }

    if (requiredLevel === LOA_TWO) {
      dispatch(navigateDefaultProviderForLevel(LOA_TWO, t));
      return;
    }

    if (requiredLevel === LOA_THREE) {
      replacePreserve(SecuritySetupRoutes.providers);
      return;
    }

    // fallback
    replacePreserve(SecuritySetupRoutes.providers);
  }, [hasRealProfile, ssoEnabled, requiredLevel, pathname, dispatch, t, replacePreserve]);
};
